import React from 'react';

/**
 * Componente Switch moderno y clickeable
 * 
 * @param {Object} props - Propiedades del componente
 * @param {boolean} props.checked - Estado del switch (activo/inactivo)
 * @param {function} props.onChange - Función callback cuando cambia el estado
 * @param {string} props.label - Texto a mostrar junto al switch
 * @param {string} props.activeLabel - Texto cuando está activo (opcional)
 * @param {string} props.inactiveLabel - Texto cuando está inactivo (opcional)
 * @param {boolean} props.disabled - Si el switch está deshabilitado
 * @param {string} props.size - Tamaño del switch ('sm', 'md', 'lg')
 * @param {string} props.className - Clases CSS adicionales
 * @returns {JSX.Element}
 */
const Switch = ({
  checked = false,
  onChange,
  label,
  activeLabel,
  inactiveLabel,
  disabled = false,
  size = 'md',
  className = ''
}) => {
  const sizeClasses = {
    sm: {
      switch: 'w-9 h-5',
      toggle: 'w-4 h-4',
      translate: 'translate-x-4'
    },
    md: {
      switch: 'w-11 h-6',
      toggle: 'w-5 h-5',
      translate: 'translate-x-5'
    },
    lg: {
      switch: 'w-14 h-7',
      toggle: 'w-6 h-6',
      translate: 'translate-x-7'
    }
  };

  const currentSize = sizeClasses[size];

  const switchBaseClasses = `
    relative inline-flex items-center rounded-full transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 cursor-pointer
    ${currentSize.switch}
    ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
    ${checked 
      ? 'bg-green-500 focus:ring-green-500' 
      : 'bg-red-500 focus:ring-red-500'
    }
  `.trim().replace(/\s+/g, ' ');

  const toggleClasses = `
    inline-block rounded-full bg-white shadow transform transition-transform duration-200 ease-in-out
    ${currentSize.toggle}
    ${checked ? currentSize.translate : 'translate-x-0.5'}
  `.trim().replace(/\s+/g, ' ');

  const handleClick = () => {
    if (!disabled && onChange) {
      onChange(!checked);
    }
  };

  const displayLabel = label || (checked ? activeLabel : inactiveLabel);

  return (
    <div className={`flex items-center space-x-3 ${className}`}>
      <button
        type="button"
        className={switchBaseClasses}
        onClick={handleClick}
        disabled={disabled}
        aria-pressed={checked}
        role="switch"
      >
        <span className={toggleClasses} />
      </button>
      
      {displayLabel && (
        <span 
          className={`text-sm font-medium cursor-pointer select-none ${
            disabled ? 'text-gray-500' : checked ? 'text-green-300' : 'text-red-300'
          }`}
          onClick={handleClick}
        >
          {displayLabel}
        </span>
      )}
    </div>
  );
};

export default Switch;